import * as THREE from 'three';
import { attachDetail, type GrainKind } from '@/rendering/DetailShading';

/**
 * Flora materials (Stage-1 stylized look, spec §18). Foliage colour is seasonally driven; the mesh
 * carries a baked grayscale shading term in its `color` attribute which Three multiplies with the
 * material colour. That split is deliberate: the geometry contributes depth and soft interior
 * occlusion, while the season model retains full authority over hue. Materials are shared where
 * possible per the performance budget.
 *
 * Each one is also bound to `DetailShading`, which gives the surface a grain of its own kind and lets
 * the sun through the thin ones. That is a *third* layer on the same split and it keeps to it: the
 * detail shading changes tone and relief and never touches hue, so the season model still owns the
 * colour of every leaf in the garden.
 */

export interface FloraMaterialOptions {
  foliageColor?: THREE.ColorRepresentation;
  trunkColor?: THREE.ColorRepresentation;
  colorVariation?: number;
  /**
   * Which surface the foliage is made of. A sedge and a hosta are the same green and want opposite
   * grains — see `DetailShading` — so the caller, which knows the plant's form, chooses.
   */
  grain?: GrainKind;
  /** Decorrelates one plant's grain from its neighbour's. An instance's `visualSeed`. */
  seed?: number;
}

/** Apply a per-instance hue/saturation/lightness nudge so a stand of one species is not uniform. */
function varyColor(color: THREE.Color, variation: number): THREE.Color {
  if (!variation) return color;
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);
  color.setHSL(
    (hsl.h + variation * 0.03 + 1) % 1,
    THREE.MathUtils.clamp(hsl.s + variation * 0.05, 0, 1),
    THREE.MathUtils.clamp(hsl.l + variation * 0.05, 0, 1),
  );
  return color;
}

export function createFoliageMaterial(options: FloraMaterialOptions = {}): THREE.MeshStandardMaterial {
  const color = varyColor(new THREE.Color(options.foliageColor ?? 0x4c7a34), options.colorVariation ?? 0);
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.88,
    metalness: 0,
    // Smooth shading + baked vertex shading reads softer and more organic than faceted foliage.
    flatShading: false,
    vertexColors: true,
    dithering: true,
  });
  attachDetail(material, options.grain ?? 'leafy', options.seed ?? 0);
  return material;
}

export function createTrunkMaterial(options: FloraMaterialOptions = {}): THREE.MeshStandardMaterial {
  const color = varyColor(new THREE.Color(options.trunkColor ?? 0x6b4b2f), (options.colorVariation ?? 0) * 0.4);
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.96,
    metalness: 0,
    flatShading: false,
    vertexColors: true,
    dithering: true,
  });
  // Always bark, whatever the foliage above it is made of — a grass tuft's stems are the one case
  // where "structure" is not woody, and at the size those are drawn nobody can tell.
  attachDetail(material, 'bark', options.seed ?? 0);
  return material;
}

/** Blossoms, seed heads and berries. Slightly glossier so flowers catch the sun against foliage. */
export function createAccentMaterial(color: THREE.ColorRepresentation = 0xe8e0d0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    roughness: 0.62,
    metalness: 0,
    flatShading: false,
    vertexColors: true,
    dithering: true,
  });
}
